import React, { useState, useEffect, useCallback, useMemo, useRef, memo } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useTab } from 'contexts/TabContext';
import { useAuth } from 'contexts/AuthContext';
import { useSessionIdle } from 'contexts/SessionIdleContext';
import useTenantCode from 'hooks/useTenantCode';
import { useErpThemeOptional } from 'contexts/ErpThemeContext';
import { resolveTenantBranding } from 'config/tenantBranding';
import { TENANT_LOGO_CHANGED_EVENT, fetchTenantLogo } from 'system/tenantLogoStore';
import { theme } from 'styles/theme';
import MenuTypeIcon from './MenuTypeIcon';
import ModuleMenuIcon, { MODULE_MENU_ICON_KEYS } from './ModuleMenuIcon';
import { useFavorites } from 'contexts/FavoritesContext';
import { HR_HOME_TAB } from 'config/hrMenuConfig';
import { flattenMenusWithPath, menuPathOf } from 'system/menuPathUtil';
import { getCachedUsers, loadUsers } from 'master/userAccountStore';
import {
  SidebarContainer,
  SidebarSearchWrap,
  SidebarSearchInput,
  SidebarHeader,
  SidebarButton,
  SidebarFilterButton,
  MenuList,
  SidebarMenuItem,
  MenuItemHeader,
  MenuItemLabel,
  ExpandIcon,
  SubMenuList,
  SidebarEmptyMessage,
  SidebarUserFooter,
  SidebarUserAvatar,
  SidebarUserInfo,
  SidebarUserName,
  SidebarUserRole,
  SidebarUserRow,
  SidebarRail,
  SidebarRailButton,
  SidebarLogoutButton,
  SidebarLogoutIconButton,
  SidebarThemeIconButton,
  SidebarToggleButton,
  SidebarLogoArea,
  SidebarLogoMark,
  SidebarLogoText,
  SidebarLogoTitle,
  SidebarLogoSub,
  FavoriteStarButton,
} from 'styles/common.styles';

const SearchHitPath = styled.div`
  font-size: ${theme.typography.fontSize.xxs};
  color: ${theme.colors.text.tertiary};
  margin-top: 2px;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/** 기관 CI — 사이드바 상단 (로그인 후) · 영역 높이에 맞춰 채우고 중앙 정렬 */
const SidebarTenantLogoImg = styled.img`
  display: block;
  height: 28px;
  max-width: ${(p) => (p.$collapsed ? '36px' : '168px')};
  width: auto;
  object-fit: contain;
  object-position: center;
  flex-shrink: 0;
  margin: 0 auto;
  border-radius: 2px;
`;

const isOpenableMenu = (item) => {
  const componentPath = item.componentPath || '';
  const url = item.url || '';
  const progId = item.progId || '';
  return Boolean(componentPath || (url !== 'dir' && (url || progId)));
};

const findTopMenuIdForItem = (allLeftMenus, menuId) => {
  if (!allLeftMenus || !menuId) return null;
  const visit = (items) => {
    for (const it of items || []) {
      if ((it.id || it.menuNo) === menuId) return true;
      if (it.children && visit(it.children)) return true;
    }
    return false;
  };
  for (const [topId, trees] of Object.entries(allLeftMenus)) {
    if (visit(trees)) return topId;
  }
  return null;
};

/** 상단 모듈 + 좌측 트리를 하나의 아코디언 트리로 구성 */
export const buildModuleMenuTree = (topMenus, allLeftMenus) => {
  return (topMenus || [])
    .filter((top) => !top.hidden)
    .map((top) => {
    const trees = allLeftMenus?.[top.id] || [];
    let children = trees;
    if (
      trees.length === 1
      && trees[0]?.children?.length
      && (trees[0].id === top.id || trees[0].label === top.label)
    ) {
      children = trees[0].children;
    }
    return {
      id: top.id,
      label: top.label || top.menuNm,
      icon: top.icon || (top.label || '?')[0],
      iconSrc: top.iconSrc || null,
      url: 'dir',
      isModule: true,
      children,
    };
  });
};

const getAllExpandableIds = (menus) => {
  const ids = [];
  (menus || []).forEach((menu) => {
    if (menu.children?.length) {
      ids.push(menu.id);
      ids.push(...getAllExpandableIds(menu.children));
    }
  });
  return ids;
};

const formatTime = (seconds) => {
  if (seconds == null) return '';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

/**
 * 전체 모듈 아코디언 메뉴 트리 + 축소 아이콘 레일
 */
const Sidebar = ({
  activeTopMenu,
  allLeftMenus = {},
  topMenus = [],
  onSwitchTopMenu,
  searchFocusNonce = 0,
  collapsed = false,
  onToggleCollapse,
  onExpandSidebar,
}) => {
  const { openTab, activeTabId, closeAllTabs } = useTab();
  const { user, logout, timeRemaining } = useAuth();
  const sessionIdle = useSessionIdle();
  const tenantCode = useTenantCode();
  const erpTheme = useErpThemeOptional();
  const colorMode = erpTheme?.colorMode || 'light';
  const [logoTick, setLogoTick] = useState(0);
  const tenantBranding = useMemo(
    () => resolveTenantBranding(tenantCode, colorMode),
    [tenantCode, colorMode, logoTick]
  );

  useEffect(() => {
    const onLogoChange = () => setLogoTick((n) => n + 1);
    window.addEventListener(TENANT_LOGO_CHANGED_EVENT, onLogoChange);
    window.addEventListener('storage', onLogoChange);
    return () => {
      window.removeEventListener(TENANT_LOGO_CHANGED_EVENT, onLogoChange);
      window.removeEventListener('storage', onLogoChange);
    };
  }, []);

  // 새 브라우저/기기에서도 서버에 저장된 로고를 1회 동기화 (로컬 캐시 비어 있을 때 대비)
  useEffect(() => {
    if (tenantCode) fetchTenantLogo(tenantCode);
  }, [tenantCode]);
  const { favorites, isFavorite, toggleFavorite } = useFavorites();
  const navigate = useNavigate();
  const [expandedMenus, setExpandedMenus] = useState({});
  const [menuFilter, setMenuFilter] = useState('');
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const searchInputRef = useRef(null);

  const moduleTree = useMemo(
    () => buildModuleMenuTree(topMenus, allLeftMenus),
    [topMenus, allLeftMenus]
  );

  // 최초·기본은 전체 접힘. 모듈 레일 클릭·전체펼치기·메뉴검색 시에만 펼침.

  useEffect(() => {
    if (!searchFocusNonce) return undefined;
    const t = window.setTimeout(() => {
      const el = searchInputRef.current;
      if (el) {
        el.focus();
        el.select();
      }
    }, 0);
    return () => window.clearTimeout(t);
  }, [searchFocusNonce]);

  const toggleMenu = (menuId) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [menuId]: !prev[menuId],
    }));
  };

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const handleSubMenuClick = useCallback((menuItem) => {
    if (!isOpenableMenu(menuItem)) return;

    const topId = findTopMenuIdForItem(allLeftMenus, menuItem.id || menuItem.menuNo);
    if (topId && topId !== activeTopMenu) {
      onSwitchTopMenu?.(topId);
    }

    const componentPath = menuItem.componentPath || '';
    const url = menuItem.url || menuItem.path || '';
    const progId = menuItem.progId || '';
    const menuId = menuItem.id || menuItem.menuNo;
    const menuLabel = menuItem.label || menuItem.menuNm || '메뉴';
    const component = menuItem.component || progId || componentPath || menuItem.id || menuItem.menuNo;

    openTab({
      id: menuId,
      label: menuLabel,
      path: url || `/${component}`,
      component,
      componentPath,
      screenId: menuItem.screenId || menuItem.progId || '',
      menuPath: menuPathOf(menuItem, allLeftMenus),
    });
  }, [allLeftMenus, activeTopMenu, onSwitchTopMenu, openTab]);

  const resolveScreenId = (item) => item?.screenId || item?.progId || '';

  const keyword = menuFilter.trim();
  const isSearching = Boolean(keyword);

  const globalSearchHits = useMemo(() => {
    if (!isSearching) return [];
    const lower = keyword.toLowerCase();
    const hits = [];

    moduleTree.forEach((mod) => {
      const flat = flattenMenusWithPath(mod.children || [], [mod.label]).filter(isOpenableMenu);
      flat.forEach((item) => {
        const label = (item.label || item.menuNm || '').toLowerCase();
        const pathText = (item.pathText || '').toLowerCase();
        const screenId = String(item.screenId || item.progId || '').toLowerCase();
        if (
          label.includes(lower)
          || pathText.includes(lower)
          || (screenId && screenId.includes(lower))
        ) {
          hits.push({
            ...item,
            topMenuId: mod.id,
            pathText: item.pathText,
          });
        }
      });
    });
    return hits;
  }, [isSearching, keyword, moduleTree]);

  const favoriteHits = useMemo(() => {
    if (!favoritesOnly || isSearching) return [];
    return favorites.map((fav) => ({
      ...fav,
      pathText: fav.screenId ? `${fav.label} · ${fav.screenId}` : fav.label,
    }));
  }, [favoritesOnly, isSearching, favorites]);

  const renderMenuItems = (menus, level = 0) => {
    return menus.map((menu) => {
      const hasChildren = menu.children && menu.children.length > 0;
      const isExpanded = Boolean(expandedMenus[menu.id]) || isSearching;
      const isActive = activeTabId === menu.id
        || (menu.isModule && activeTopMenu === menu.id && !activeTabId);
      const isModuleActive = menu.isModule && activeTopMenu === menu.id;
      const screenId = resolveScreenId(menu);
      const fav = screenId ? isFavorite(screenId) : false;

      return (
        <SidebarMenuItem key={menu.id || menu.menuNo}>
          <MenuItemHeader
            onClick={() => {
              if (hasChildren) {
                if (menu.isModule) {
                  onSwitchTopMenu?.(menu.id);
                }
                toggleMenu(menu.id);
              } else {
                handleSubMenuClick(menu);
              }
            }}
            $active={isActive || isModuleActive}
            $level={level}
          >
            {!hasChildren && <MenuTypeIcon menuType={menu.menuType} />}
            <MenuItemLabel $fontWeight={hasChildren ? '600' : '400'}>
              {menu.label || menu.menuNm}
            </MenuItemLabel>
            {!hasChildren && screenId && (
              <FavoriteStarButton
                type="button"
                $active={fav}
                title={fav ? '즐겨찾기 해제' : '즐겨찾기 추가'}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFavorite(menu);
                }}
              >
                {fav ? '★' : '☆'}
              </FavoriteStarButton>
            )}
            {hasChildren && (
              <ExpandIcon $expanded={isExpanded}>▶</ExpandIcon>
            )}
          </MenuItemHeader>
          {hasChildren && (
            <SubMenuList $expanded={isExpanded}>
              {renderMenuItems(menu.children, level + 1)}
            </SubMenuList>
          )}
        </SidebarMenuItem>
      );
    });
  };

  const renderSearchHits = () => {
    if (!globalSearchHits.length) {
      return <SidebarEmptyMessage>검색 결과가 없습니다</SidebarEmptyMessage>;
    }
    return globalSearchHits.map((hit) => {
      const isActive = activeTabId === (hit.id || hit.menuNo);
      const screenId = resolveScreenId(hit);
      const fav = screenId ? isFavorite(screenId) : false;
      return (
        <SidebarMenuItem key={`hit-${hit.id || hit.menuNo}-${screenId}`}>
          <MenuItemHeader
            onClick={() => handleSubMenuClick(hit)}
            $active={isActive}
            $level={0}
            title={`${hit.pathText}${screenId ? ` [${screenId}]` : ''}`}
          >
            <MenuTypeIcon menuType={hit.menuType} />
            <div style={{ minWidth: 0, flex: 1 }}>
              <MenuItemLabel $fontWeight="500">
                {hit.label || hit.menuNm}
              </MenuItemLabel>
              <SearchHitPath>
                {hit.pathText}
                {screenId ? ` · ${screenId}` : ''}
              </SearchHitPath>
            </div>
            {screenId && (
              <FavoriteStarButton
                type="button"
                $active={fav}
                title={fav ? '즐겨찾기 해제' : '즐겨찾기 추가'}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFavorite(hit);
                }}
              >
                {fav ? '★' : '☆'}
              </FavoriteStarButton>
            )}
          </MenuItemHeader>
        </SidebarMenuItem>
      );
    });
  };

  const renderFavoriteList = () => {
    if (!favoriteHits.length) {
      return (
        <SidebarEmptyMessage>
          즐겨찾기가 없습니다. 메뉴의 ☆를 눌러 추가하세요.
        </SidebarEmptyMessage>
      );
    }
    return favoriteHits.map((hit) => {
      const isActive = activeTabId === (hit.id || hit.menuNo);
      const screenId = hit.screenId;
      return (
        <SidebarMenuItem key={`fav-${screenId}`}>
          <MenuItemHeader
            onClick={() => handleSubMenuClick(hit)}
            $active={isActive}
            $level={0}
            title={hit.pathText}
          >
            <MenuTypeIcon menuType={hit.menuType} />
            <div style={{ minWidth: 0, flex: 1 }}>
              <MenuItemLabel $fontWeight="500">{hit.label}</MenuItemLabel>
              <SearchHitPath>{screenId}</SearchHitPath>
            </div>
            <FavoriteStarButton
              type="button"
              $active
              title="즐겨찾기 해제"
              onClick={(e) => {
                e.stopPropagation();
                toggleFavorite(hit);
              }}
            >
              ★
            </FavoriteStarButton>
          </MenuItemHeader>
        </SidebarMenuItem>
      );
    });
  };

  const userName = user?.name || user?.username || '관리자';
  const [deptLabel, setDeptLabel] = useState('');
  useEffect(() => {
    let cancelled = false;
    const loginId = String(user?.id || user?.username || '').trim();
    if (!loginId || !tenantCode) {
      setDeptLabel('');
      return undefined;
    }
    const apply = (rows) => {
      const hit = (rows || []).find((u) => String(u.loginId || '').trim() === loginId);
      if (!hit) {
        setDeptLabel('');
        return;
      }
      if (hit.deptCode && hit.deptName) setDeptLabel(`${hit.deptCode} · ${hit.deptName}`);
      else setDeptLabel(hit.deptName || hit.deptCode || '');
    };
    apply(getCachedUsers(tenantCode));
    loadUsers(tenantCode, user).then((rows) => {
      if (!cancelled) apply(rows);
    });
    return () => {
      cancelled = true;
    };
  }, [tenantCode, user]);
  const sessionText = timeRemaining != null ? formatTime(timeRemaining) : '';
  const userInitial = userName[0];
  const userTitle = [userName, deptLabel || '부서 미지정', sessionText].filter(Boolean).join(' · ');

  const handleRailModuleClick = (mod) => {
    onSwitchTopMenu?.(mod.id);
    setExpandedMenus((prev) => ({ ...prev, [mod.id]: true }));
    onExpandSidebar?.();
  };

  const allExpandableIds = useMemo(
    () => getAllExpandableIds(moduleTree),
    [moduleTree]
  );

  const isAllExpanded = useMemo(() => {
    if (!allExpandableIds.length) return false;
    return allExpandableIds.every((id) => expandedMenus[id]);
  }, [allExpandableIds, expandedMenus]);

  const toggleExpandAll = useCallback(() => {
    if (isAllExpanded) {
      setExpandedMenus({});
      return;
    }
    const all = {};
    allExpandableIds.forEach((id) => { all[id] = true; });
    setExpandedMenus(all);
  }, [isAllExpanded, allExpandableIds]);

  if (!moduleTree.length) {
    return (
      <SidebarContainer $collapsed={collapsed}>
        <SidebarEmptyMessage>메뉴를 선택해주세요</SidebarEmptyMessage>
      </SidebarContainer>
    );
  }

  return (
    <SidebarContainer $collapsed={collapsed}>
      <SidebarLogoArea
        $collapsed={collapsed}
        $dark={!!tenantBranding?.logoAreaDark}
        $colorModeDark={colorMode === 'dark'}
        title={tenantBranding?.logoAlt || 'ERP 인사(HR)'}
      >
        {tenantBranding?.logoUrl ? (
          <SidebarTenantLogoImg
            src={tenantBranding.logoUrl}
            alt={tenantBranding.logoAlt || '기관 로고'}
            $collapsed={collapsed}
          />
        ) : (
          <>
            <SidebarLogoMark>HR</SidebarLogoMark>
            {!collapsed && (
              <SidebarLogoText>
                <SidebarLogoTitle>ERP 인사</SidebarLogoTitle>
                <SidebarLogoSub>HR Module</SidebarLogoSub>
              </SidebarLogoText>
            )}
          </>
        )}
      </SidebarLogoArea>

      <SidebarSearchWrap $collapsed={collapsed}>
        <SidebarToggleButton
          type="button"
          onClick={onToggleCollapse}
          title={collapsed ? '메뉴 펼치기' : '메뉴 아이콘으로 접기'}
        >
          ☰
        </SidebarToggleButton>
        {!collapsed && (
          <SidebarSearchInput
            ref={searchInputRef}
            type="search"
            placeholder="메뉴명·화면ID 검색 (Ctrl+F)"
            title="Ctrl+F — 메뉴명 또는 screenId로 검색"
            value={menuFilter}
            onChange={(e) => setMenuFilter(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setMenuFilter('');
                e.currentTarget.blur();
              }
            }}
            style={{ flex: 1, minWidth: 0 }}
          />
        )}
      </SidebarSearchWrap>

      <SidebarHeader $collapsed={collapsed}>
        <SidebarThemeIconButton
          type="button"
          $active={colorMode === 'dark'}
          title={colorMode === 'dark' ? '라이트 모드로 전환 (개인)' : '다크 모드로 전환 (개인)'}
          onClick={() => erpTheme?.setColorMode?.(colorMode === 'dark' ? 'light' : 'dark')}
          aria-label={colorMode === 'dark' ? '라이트 모드' : '다크 모드'}
        >
          {colorMode === 'dark' ? '☀' : '☾'}
        </SidebarThemeIconButton>
        {!collapsed && (
          <>
            <SidebarFilterButton
              type="button"
              $active={favoritesOnly}
              title="즐겨찾기만 보기"
              onClick={() => setFavoritesOnly((v) => !v)}
            >
              ★즐겨찾기
            </SidebarFilterButton>
            <SidebarFilterButton
              type="button"
              $active={isAllExpanded}
              title={isAllExpanded ? '메뉴 모두 접기' : '메뉴 모두 펼치기'}
              onClick={toggleExpandAll}
            >
              {isAllExpanded ? '접기' : '펼치기'}
            </SidebarFilterButton>
            <SidebarButton
              onClick={() => closeAllTabs(HR_HOME_TAB)}
              title="모든 탭을 닫고 메인화면을 엽니다"
            >
              탭닫기
            </SidebarButton>
          </>
        )}
      </SidebarHeader>

      {!collapsed && (
        <MenuList>
          {isSearching
            ? renderSearchHits()
            : favoritesOnly
              ? renderFavoriteList()
              : renderMenuItems(moduleTree)}
        </MenuList>
      )}

      {collapsed && (
        <SidebarRail>
          {moduleTree.map((mod) => (
            <SidebarRailButton
              key={mod.id}
              type="button"
              title={mod.label}
              $active={activeTopMenu === mod.id}
              onClick={() => handleRailModuleClick(mod)}
            >
              {mod.iconSrc ? (
                <img src={mod.iconSrc} alt={mod.label} style={{ width: 22, height: 22, objectFit: 'contain' }} />
              ) : MODULE_MENU_ICON_KEYS.includes(mod.icon) ? (
                <ModuleMenuIcon name={mod.icon} size={22} />
              ) : (
                (mod.label || '?')[0]
              )}
            </SidebarRailButton>
          ))}
        </SidebarRail>
      )}

      <SidebarUserFooter $collapsed={collapsed}>
        {collapsed ? (
          <>
            <SidebarUserAvatar title={userTitle}>{userInitial}</SidebarUserAvatar>
            {sessionIdle.enabled ? (
              <SidebarLogoutIconButton
                type="button"
                title="지금 잠금 (Ctrl+Shift+L)"
                onClick={sessionIdle.lockNow}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <rect x="5" y="11" width="14" height="10" rx="2" />
                  <path d="M8 11V8a4 4 0 0 1 8 0v3" />
                </svg>
              </SidebarLogoutIconButton>
            ) : null}
            <SidebarLogoutIconButton type="button" title="로그아웃" $danger onClick={handleLogout}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M12 2v10" />
                <path d="M6.3 6.3a8 8 0 1 0 11.4 0" />
              </svg>
            </SidebarLogoutIconButton>
          </>
        ) : (
          <>
            <SidebarUserRow>
              <SidebarUserAvatar>{userInitial}</SidebarUserAvatar>
              <SidebarUserInfo>
                <SidebarUserName title={userName}>{userName}</SidebarUserName>
                <SidebarUserRole>
                  {deptLabel || '부서 미지정'}
                  {sessionText ? ` · ${sessionText}` : ''}
                </SidebarUserRole>
              </SidebarUserInfo>
            </SidebarUserRow>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
              {sessionIdle.enabled ? (
                <SidebarLogoutButton
                  type="button"
                  onClick={sessionIdle.lockNow}
                  title="Ctrl+Shift+L"
                >
                  지금 잠금
                </SidebarLogoutButton>
              ) : null}
              <SidebarLogoutButton type="button" onClick={handleLogout}>
                로그아웃
              </SidebarLogoutButton>
            </div>
          </>
        )}
      </SidebarUserFooter>
    </SidebarContainer>
  );
};

export default memo(Sidebar);
